import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

import { Search } from "lucide-react";
import { Claim } from "@/types";
import { formatCurrency, formatDate, getStatusBadgeClass } from "@/lib/utils";
import { useTenantCurrency } from "@/hooks/use-tenant";
import { formatCurrencyWithTenantAndConversion } from "@/lib/currency-conversion";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface ClaimsListProps {
  projectId: string;
}

export default function ClaimsList({ projectId }: ClaimsListProps) {
  const navigate = useLocation()[1];
  const { currency: tenantCurrency } = useTenantCurrency();
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  
  // Fetch claims for this project
  const { data: claims, isLoading } = useQuery<Claim[]>({
    queryKey: [`/api/projects/${projectId}/claims`],
  });
  
  // Filter claims based on search term and filters
  const filteredClaims = claims?.filter(claim => {
    const matchesSearch = 
      !searchTerm || 
      claim.claimIdentifier?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      claim.claimHeadline.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = !statusFilter || statusFilter === "all" || claim.status === statusFilter;
    const matchesType = !typeFilter || typeFilter === "all" || claim.claimType === typeFilter;
    
    return matchesSearch && matchesStatus && matchesType;
  });
  
  // Get unique claim statuses and types for filters
  const claimStatusesSet = new Set<string>();
  const claimTypesSet = new Set<string>();
  
  if (claims) {
    claims.forEach(claim => {
      // Status should always be present, but check anyway
      if (claim.status) claimStatusesSet.add(claim.status);
      
      // Only add claim types that are defined
      if (claim.claimType && typeof claim.claimType === 'string') {
        claimTypesSet.add(claim.claimType);
      }
    });
  }
  
  const claimStatuses = Array.from(claimStatusesSet);
  const claimTypes = Array.from(claimTypesSet);
  
  // Create new claim
  // const handleCreateClaim = () => {
  //   navigate(`/projects/${projectId}/new-claim`);
  // };

  // Go to claim detail page
  const handleViewClaim = (claimId: string) => {
    navigate(`/claims/${claimId}`);
  };
  
  return (
    <div className="mt-6">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h2 className="text-xl font-semibold text-neutral-800">Claims</h2>
          <p className="mt-2 text-sm text-neutral-600">
            A list of all claims submitted for this project including their status, submitted amount, and response due date.
          </p>
        </div>
      </div>
      
      {/* Filter and Search */}
      <div className="mt-4 flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-400" size={18} />
          <Input
            type="search"
            placeholder="Search claims..."
            className="pl-10"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        
        <div className="flex gap-4">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {claimStatuses.map(status => (
                <SelectItem key={status} value={status}>{status}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="All Types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {claimTypes.map(type => (
                <SelectItem key={type} value={type}>{type}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      
      {/* Claims Table */}
      <div className="mt-6 flex flex-col">
        <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
            <div className="overflow-hidden shadow md:rounded-lg">
              <table className="min-w-full divide-y divide-neutral-200">
                <thead className="bg-neutral-50">
                  <tr>
                    <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-neutral-800 sm:pl-6">
                      Claim ID
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-neutral-800">
                      Type
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-neutral-800 hidden md:table-cell">
                      Description
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-neutral-800">
                      Status
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-neutral-800 hidden sm:table-cell">
                      Creation
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-neutral-800 hidden sm:table-cell">
                      Due Date
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-neutral-800">
                      Amount
                    </th>
                    <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-200 bg-white">
                  {isLoading ? (
                    // Loading state
                    Array.from({ length: 5 }).map((_, i) => (
                      <tr key={i}>
                        <td className="whitespace-nowrap py-4 pl-4 pr-3 sm:pl-6">
                          <div className="bg-muted animate-pulse rounded-md h-5 w-20" />
                        </td>
                        <td className="px-3 py-4">
                          <div className="bg-muted animate-pulse rounded-md h-5 w-24" />
                        </td>
                        <td className="px-3 py-4">
                          <div className="bg-muted animate-pulse rounded-md h-5 w-40" />
                        </td>
                        <td className="px-3 py-4">
                          <div className="bg-muted animate-pulse rounded-md h-5 w-24" />
                        </td>
                        <td className="px-3 py-4">
                          <div className="bg-muted animate-pulse rounded-md h-5 w-24" />
                        </td>
                        <td className="px-3 py-4">
                          <div className="bg-muted animate-pulse rounded-md h-5 w-24" />
                        </td>
                        <td className="px-3 py-4 text-right">
                          <div className="bg-muted animate-pulse rounded-md h-5 w-16 ml-auto" />
                        </td>
                      </tr>
                    ))
                  ) : filteredClaims && filteredClaims.length > 0 ? (
                    // Claims list
                    filteredClaims.map((claim) => {
                      return (
                        <tr 
                          key={claim.claimId} 
                          className="hover:bg-neutral-50 cursor-pointer"
                          onClick={() => handleViewClaim(claim.claimId)}
                        >
                          <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-neutral-800 sm:pl-6">
                            {claim.claimIdentifier || "-"}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-neutral-600">
                            {claim.claimType || "-"}
                          </td>
                          <td className="px-3 py-4 text-sm text-neutral-600 max-w-xs truncate hidden md:table-cell">
                            {claim.claimHeadline}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm">
                            <span className={getStatusBadgeClass(claim.status)}>
                              {claim.status}
                            </span>
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-neutral-600 hidden sm:table-cell">
                            {formatDate(claim.createdAt)}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-neutral-600 hidden sm:table-cell">
                            -
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm font-medium text-neutral-800">
                            <div>
                              {formatCurrencyWithTenantAndConversion(claim.claimAmount, claim.claimAmountCurrency, tenantCurrency, {
                                showSymbol: true,
                                showOriginal: false, // Keep table compact
                                showConversionRate: false
                              })}
                              {claim.claimAmountCurrency !== tenantCurrency && (
                                <div className="text-xs text-blue-600">
                                  💱 {claim.claimAmountCurrency}
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                            {/* Action column kept empty but maintained for consistent layout */}
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    // No results
                    <tr>
                      <td colSpan={8} className="py-8 text-center text-sm text-neutral-500">
                        No claims found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      
      {/* Pagination - Simplified for now */}
      <div className="mt-6">
        <nav className="flex items-center justify-between border-t border-neutral-200 px-4 sm:px-0">
          <div className="-mt-px flex w-0 flex-1">
            <Button variant="link" disabled className="inline-flex items-center pt-4 pr-1 text-sm font-medium text-neutral-500">
              <svg className="mr-3 h-5 w-5 text-neutral-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z" clipRule="evenodd" />
              </svg>
              Previous
            </Button>
          </div>
          <div className="hidden md:-mt-px md:flex">
            <Button variant="link" className="inline-flex items-center border-t-2 border-primary pt-4 px-4 text-sm font-medium text-primary" aria-current="page">
              1
            </Button>
          </div>
          <div className="-mt-px flex w-0 flex-1 justify-end">
            <Button variant="link" disabled className="inline-flex items-center pt-4 pl-1 text-sm font-medium text-neutral-500">
              Next
              <svg className="ml-3 h-5 w-5 text-neutral-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
              </svg>
            </Button>
          </div>
        </nav>
      </div>
      {/* No modal needed - claim detail page is accessed directly by clicking on a row */}
    </div>
  );
}
